import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiGrid, FiHome, FiLayers, FiTool, FiTruck, FiCalendar,
    FiChevronRight, FiClock, FiPackage
} from "react-icons/fi";
import { BrickWall, Grid2X2 } from "lucide-react";
import { GiWoodBeam } from "react-icons/gi";

const ScheduleHub = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const scheduleTypes = [
        {
            id: 'editor',
            title: 'Job Editor',
            description: 'Comprehensive view of all jobs with advanced filtering and management',
            icon: FiGrid,
            color: 'blue',
            gradient: 'from-blue-500 to-blue-600',
            bgGradient: 'from-blue-50 to-blue-100',
            path: 'editor',
            features: ['All jobs', 'Advanced filters', 'Bulk actions', 'Detailed view']
        },
        {
            id: 'production',
            title: 'Production Schedule',
            description: 'Track production progress and manage cutting and manufacturing',
            icon: FiTool,
            color: 'purple',
            gradient: 'from-purple-500 to-purple-600',
            bgGradient: 'from-purple-50 to-purple-100',
            path: 'production',
            features: ['Production tracking', 'Phase management', 'Timeline view', 'Completion status']
        },
        {
            id: 'delivery',
            title: 'Delivery Schedule',
            description: 'Manage deliveries and assign drivers to completed jobs',
            icon: FiTruck,
            color: 'green',
            gradient: 'from-green-500 to-green-600',
            bgGradient: 'from-green-50 to-green-100',
            path: 'delivery',
            features: ['Driver assignment', 'Delivery tracking', 'Route planning', 'Status updates']
        }
    ];

    const quoteSchedules = [
        {
            id: 'roof',
            title: 'Roof Schedule',
            description: 'Roof design and estimation scheduling',
            icon: FiHome,
            color: 'blue',
            gradient: 'from-blue-400 to-blue-500',
            path: 'roof'
        },
        {
            id: 'wall',
            title: 'Wall Schedule',
            description: 'Wall design and estimation scheduling',
            icon: BrickWall,
            color: 'purple',
            gradient: 'from-purple-400 to-purple-500',
            path: 'wall'
        },
        {
            id: 'floor',
            title: 'Floor Schedule',
            description: 'Floor design and estimation scheduling',
            icon: Grid2X2,
            color: 'green',
            gradient: 'from-green-400 to-green-500',
            path: 'floor'
        },
        {
            id: 'timber',
            title: 'Timber Schedule',
            description: 'Timber job scheduling and tracking',
            icon: GiWoodBeam,
            color: 'amber',
            gradient: 'from-amber-400 to-amber-500',
            path: 'timber'
        }
    ];

    const handleNavigate = (path) => {
        navigate(`/${companySlug}/schedule/${path}`);
    };

    return (
        <>
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                        <FiCalendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Schedule Management</h1>
                        <p className="text-gray-600 mt-1">
                            Choose a schedule type to view and manage your construction jobs
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Schedule Types */}
            <div className="mb-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-gray-600" />
                    Primary Schedules
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {scheduleTypes.map((schedule) => {
                        const Icon = schedule.icon;
                        return (
                            <button
                                key={schedule.id}
                                onClick={() => handleNavigate(schedule.path)}
                                className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden text-left"
                            >
                                {/* Gradient Background */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${schedule.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>

                                <div className="relative p-6">
                                    {/* Icon and Title */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`p-3 bg-gradient-to-br ${schedule.gradient} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        {schedule.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {schedule.description}
                                    </p>

                                    {/* Features */}
                                    <div className="space-y-1.5">
                                        {schedule.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs text-gray-500">
                                                <div className={`w-1.5 h-1.5 rounded-full bg-${schedule.color}-500`}></div>
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Bottom accent */}
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${schedule.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Quote Type Schedules */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiPackage className="w-5 h-5 text-gray-600" />
                    Quote Type Schedules
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quoteSchedules.map((schedule) => {
                        const Icon = schedule.icon;
                        return (
                            <button
                                key={schedule.id}
                                onClick={() => handleNavigate(schedule.path)}
                                className="group relative bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden text-left p-5"
                            >
                                {/* Icon */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className={`p-2.5 bg-gradient-to-br ${schedule.gradient} rounded-lg shadow group-hover:scale-110 transition-transform duration-300`}>
                                        <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                                </div>

                                {/* Content */}
                                <h3 className="text-base font-bold text-gray-900 mb-1">
                                    {schedule.title}
                                </h3>
                                <p className="text-xs text-gray-600">
                                    {schedule.description}
                                </p>

                                {/* Bottom accent */}
                                <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${schedule.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Info Card */}
            <div className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-6">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <FiCalendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                            Schedule Management Tips
                        </h3>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Use the Job Editor for comprehensive job management and filtering</li>
                            <li>• Production Schedule tracks cutting and manufacturing phases</li>
                            <li>• Delivery Schedule helps assign drivers and manage completed jobs</li>
                            <li>• Quote Type Schedules provide focused views for specific work types</li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ScheduleHub;